import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import PlatformOwnerBadge from '../../components/ui/PlatformOwnerBadge';
import { COIN_PACKS, createCheckoutSession, getUserSubscription, getCoinBalance, getPurchaseHistory,  } from '../../services/billingService';
import { COMPUTE_PLANS } from '../../services/computeWallet';

const COIN_COSTS = {
  image_generation: 10,
  animation_clip: 50,
  scene_batch: 60,
  full_music_video: 300,
};

function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isPlatformOwner, isSuperAdmin, simulatedTier, setSimulatedTier, effectiveTier } = useAuth();

  const [activeTab, setActiveTab] = useState('subscriptions');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [coinBalance, setCoinBalance] = useState({ balance: 0, lifetimeEarned: 0 });
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [coinTransactions, setCoinTransactions] = useState([]);

  const TABS = [
    { id: 'subscriptions', label: 'Subscription Plans', icon: 'CreditCard' },
    { id: 'coins', label: 'Coin Packs', icon: 'Coins' },
    { id: 'generation', label: 'Generation Costs', icon: 'Cpu' },
    { id: 'history', label: 'Purchase History', icon: 'Receipt' },
  ];

  // Compute-wallet aware plan definitions matching COMPUTE_PLANS
  const PLATFORM_PLANS = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      priceDisplay: '$0',
      period: 'forever',
      color: '#6B7280',
      icon: 'User',
      stripePriceId: null,
      features: [
        '120 video seconds / period',
        '2 music generations',
        '1 game generation',
        'Watermark on exports',
        'Community access',
      ],
      compute: COMPUTE_PLANS?.free,
    },
    {
      id: 'creator',
      name: 'Creator',
      price: 12,
      priceDisplay: '$12',
      period: 'month',
      color: '#6366F1',
      icon: 'Zap',
      popular: true,
      stripePriceId: 'pro_monthly',
      features: [
        '1,800 video seconds / month',
        '20 music generations',
        '10 game generations',
        'No watermark',
        'HD exports',
        'Priority generation',
      ],
      compute: COMPUTE_PLANS?.creator,
    },
    {
      id: 'studio',
      name: 'Studio',
      price: 29,
      priceDisplay: '$29',
      period: 'month',
      color: '#F59E0B',
      icon: 'Star',
      stripePriceId: 'studio_monthly',
      features: [
        '7,200 video seconds / month',
        '100 music generations',
        '40 game generations',
        'No watermark',
        'HD exports',
        'Priority GPU queue',
        '4K exports',
      ],
      compute: COMPUTE_PLANS?.studio,
    },
    {
      id: 'pro_creator',
      name: 'Pro Creator',
      price: 59,
      priceDisplay: '$59',
      period: 'month',
      color: '#10B981',
      icon: 'Crown',
      stripePriceId: 'enterprise_monthly',
      features: [
        '20,000 video seconds / month',
        '300 music generations',
        '100 game generations',
        'No watermark',
        'HD + 4K exports',
        'Priority GPU queue',
        'API access',
      ],
      compute: COMPUTE_PLANS?.pro_creator,
    },
  ];

  // Handle return from Stripe checkout
  useEffect(() => {
    const status = searchParams?.get('status');
    const sessionId = searchParams?.get('session_id');
    if (status === 'success' && sessionId) {
      setToast({ type: 'success', message: 'Payment successful! Your purchase has been processed.' });
      if (user?.id) loadUserData(user?.id);
    } else if (status === 'canceled') {
      setToast({ type: 'info', message: 'Checkout was canceled. No charge was made.' });
    }
  }, [searchParams, user]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  useEffect(() => {
    if (user?.id) loadUserData(user?.id);
  }, [user]);

  const loadUserData = async (userId) => {
    setLoadingData(true);
    try {
      const [sub, coins, history] = await Promise.all([
        getUserSubscription(userId),
        getCoinBalance(userId),
        getPurchaseHistory(userId),
      ]);
      setCurrentSubscription(sub);
      setCoinBalance(coins);
      setPurchaseHistory(history);
    } catch (err) {
      console.error('loadUserData error:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleCheckout = async (productId) => {
    if (!isAuthenticated) {
      setToast({ type: 'error', message: 'Please sign in to make a purchase.' });
      return;
    }

    // ADMIN OVERRIDE: Platform owner never needs Stripe
    if (isPlatformOwner || isSuperAdmin) {
      setToast({ type: 'success', message: 'Platform Owner Mode: Full access is permanently granted. No payment required.' });
      return;
    }

    setCheckoutLoading(productId);
    try {
      const { url, error, adminOverride } = await createCheckoutSession(productId);
      if (adminOverride) {
        setToast({ type: 'success', message: 'Platform Owner Mode: No payment required.' });
        return;
      }
      if (error) {
        setToast({ type: 'error', message: error });
        return;
      }
      if (url) window.location.href = url;
    } catch (err) {
      setToast({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setCheckoutLoading(null);
    }
  };

  // For display: use simulated tier if platform owner is testing, otherwise real tier
  const displayTier = isPlatformOwner ? (simulatedTier || 'enterprise') : (currentSubscription?.tier || 'free');
  const currentTier = displayTier;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-20 right-4 z-[300] flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl"
          style={{
            background: toast?.type === 'success' ? 'rgba(16,185,129,0.15)' : toast?.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(99,102,241,0.15)',
            border: `1px solid ${toast?.type === 'success' ? '#10B981' : toast?.type === 'error' ? '#EF4444' : '#6366F1'}40`,
            backdropFilter: 'blur(12px)',
          }}
        >
          <Icon
            name={toast?.type === 'success' ? 'CheckCircle2' : toast?.type === 'error' ? 'AlertCircle' : 'Info'}
            size={18}
            color={toast?.type === 'success' ? '#10B981' : toast?.type === 'error' ? '#EF4444' : '#6366F1'}
          />
          <span className="text-sm font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
            {toast?.message}
          </span>
          <button onClick={() => setToast(null)}>
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
      )}
      <div className="tab-nav-offset">
        <div className="max-w-5xl mx-auto px-4 py-8">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)' }}>
              <Icon name="Zap" size={14} color="#6366F1" />
              <span className="text-xs font-semibold" style={{ color: '#6366F1', fontFamily: 'var(--font-caption)' }}>LoopNest Plans</span>
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              Unlock Your Creative Potential
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              Choose a plan or top up coins to power your AI creations
            </p>
          </div>

          {/* Platform Owner Badge */}
          {isPlatformOwner && (
            <div className="mb-6">
              <PlatformOwnerBadge />
            </div>
          )}

          {/* User Status Bar */}
          {isAuthenticated && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl mb-6"
              style={{ background: 'var(--color-card)', border: `1px solid ${isPlatformOwner ? 'rgba(16,185,129,0.3)' : 'var(--color-border)'}` }}
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
                    <Icon name="Coins" size={16} color="#F59E0B" />
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Coin Balance</p>
                    <p className="text-base font-bold" style={{ color: '#F59E0B', fontFamily: 'var(--font-data)' }}>
                      {isPlatformOwner ? '∞' : (loadingData ? '...' : coinBalance?.balance?.toLocaleString())}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: isPlatformOwner ? 'rgba(16,185,129,0.15)' : 'rgba(99,102,241,0.15)' }}>
                    <Icon name={isPlatformOwner ? 'ShieldCheck' : 'Crown'} size={16} color={isPlatformOwner ? '#10B981' : '#6366F1'} />
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Current Plan</p>
                    <p className="text-base font-bold capitalize" style={{ color: isPlatformOwner ? '#10B981' : '#6366F1', fontFamily: 'var(--font-data)' }}>
                      {isPlatformOwner ? `Enterprise (Owner)` : (loadingData ? '...' : currentTier)}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('history')}
                className="text-xs px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
                style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
              >
                View History
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-xl mb-6" style={{ background: 'var(--color-muted)' }}>
            {[{ id: 'subscriptions', label: 'Subscription Plans', icon: 'Crown' }, { id: 'coins', label: 'Coin Packs', icon: 'Coins' }, { id: 'generation', label: 'Generation Costs', icon: 'Cpu' }, { id: 'history', label: 'Purchase History', icon: 'Receipt' }]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: activeTab === tab?.id ? 'var(--color-card)' : 'transparent',
                  color: activeTab === tab?.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                  fontFamily: 'var(--font-heading)',
                  boxShadow: activeTab === tab?.id ? 'var(--shadow-sm)' : 'none',
                }}
              >
                <Icon name={tab?.icon} size={14} color={activeTab === tab?.id ? 'var(--color-primary)' : 'currentColor'} />
                <span className="hidden sm:inline">{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <div>
              {/* Compute quota comparison */}
              <div className="mb-6 p-4 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Cpu" size={16} color="var(--color-primary)" />
                  <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Compute Quotas by Plan</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        {['', 'Free', 'Creator', 'Studio', 'Pro Creator']?.map((h) => (
                          <th key={h} className="text-left py-2 pr-4 font-semibold" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Video Seconds', values: ['120s', '1,800s', '7,200s', '20,000s'] },
                        { label: 'Music Generations', values: ['2', '20', '100', '300'] },
                        { label: 'Game Generations', values: ['1', '10', '40', '100'] },
                        { label: 'Price', values: ['$0', '$12/mo', '$29/mo', '$59/mo'] },
                      ]?.map((row) => (
                        <tr key={row?.label} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td className="py-2 pr-4 font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{row?.label}</td>
                          {row?.values?.map((v, i) => (
                            <td key={i} className="py-2 pr-4" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {PLATFORM_PLANS?.map((tier) => {
                  const isCurrentTier = currentTier === tier?.id || (tier?.id === 'creator' && currentTier === 'pro');
                  const isLoading = checkoutLoading === tier?.stripePriceId;
                  const isSimulating = isPlatformOwner && simulatedTier === tier?.id;
                  return (
                    <div
                      key={tier?.id}
                      className="relative flex flex-col rounded-2xl p-5 transition-all"
                      style={{
                        background: 'var(--color-card)',
                        border: `2px solid ${isCurrentTier ? tier?.color : isSimulating ? `${tier?.color}80` : tier?.popular ? `${tier?.color}40` : 'var(--color-border)'}`,
                        boxShadow: tier?.popular ? `0 0 24px ${tier?.color}20` : 'none',
                      }}
                    >
                      {tier?.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold" style={{ background: tier?.color, color: 'white', fontFamily: 'var(--font-caption)' }}>
                          Most Popular
                        </div>
                      )}
                      {isCurrentTier && (
                        <div className="absolute -top-3 right-3 px-3 py-1 rounded-full text-xs font-bold" style={{ background: tier?.color, color: 'white', fontFamily: 'var(--font-caption)' }}>
                          {isPlatformOwner ? 'Simulating' : 'Current'}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${tier?.color}20` }}>
                          <Icon name={tier?.icon} size={18} color={tier?.color} />
                        </div>
                        <div>
                          <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{tier?.name}</p>
                          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                            {tier?.price === 0 ? 'Free forever' : `per ${tier?.period}`}
                          </p>
                        </div>
                      </div>
                      <div className="mb-3">
                        <span className="text-2xl font-bold" style={{ color: tier?.color, fontFamily: 'var(--font-data)' }}>{tier?.priceDisplay}</span>
                        {tier?.price > 0 && <span className="text-xs ml-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>/{tier?.period}</span>}
                      </div>
                      {/* Compute quota highlights */}
                      <div className="flex flex-col gap-1.5 mb-3 p-3 rounded-xl" style={{ background: `${tier?.color}08`, border: `1px solid ${tier?.color}20` }}>
                        {[
                          { icon: 'Video', label: `${tier?.compute?.videoSeconds?.toLocaleString()}s video` },
                          { icon: 'Music', label: `${tier?.compute?.musicGenerations} music` },
                          { icon: 'Gamepad2', label: `${tier?.compute?.gameGenerations} games` },
                        ]?.map((q) => (
                          <div key={q?.label} className="flex items-center gap-1.5">
                            <Icon name={q?.icon} size={11} color={tier?.color} />
                            <span className="text-xs font-semibold" style={{ color: tier?.color, fontFamily: 'var(--font-data)' }}>{q?.label}</span>
                          </div>
                        ))}
                      </div>
                      <ul className="flex flex-col gap-1.5 mb-5 flex-1">
                        {tier?.features?.map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Icon name="Check" size={13} color={tier?.color} />
                            <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{f}</span>
                          </li>
                        ))}
                      </ul>
                      {/* CTA */}
                      {isPlatformOwner ? (
                        <button
                          onClick={() => setSimulatedTier(tier?.id)}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                          style={{ background: isCurrentTier ? tier?.color : 'var(--color-muted)', color: isCurrentTier ? 'white' : 'var(--color-muted-foreground)', fontFamily: 'var(--font-heading)' }}
                        >
                          <Icon name="Eye" size={14} color={isCurrentTier ? 'white' : 'var(--color-muted-foreground)'} />
                          {isCurrentTier ? 'Simulating This Plan' : 'Simulate This Plan'}
                        </button>
                      ) : tier?.id === 'free' ? (
                        <div className="w-full py-2.5 rounded-xl text-sm font-semibold text-center" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-heading)' }}>
                          {isCurrentTier ? 'Current Plan' : 'Free Plan'}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleCheckout(tier?.stripePriceId)}
                          disabled={isCurrentTier || isLoading}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-60"
                          style={{ background: isCurrentTier ? 'var(--color-muted)' : tier?.color, color: isCurrentTier ? 'var(--color-muted-foreground)' : 'white', fontFamily: 'var(--font-heading)' }}
                        >
                          {isLoading ? (
                            <><Icon name="Loader" size={14} color="white" /> Processing...</>
                          ) : isCurrentTier ? (
                            'Current Plan'
                          ) : (
                            <><Icon name="CreditCard" size={14} color="white" /> Subscribe</>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Coin Packs Tab */}
          {activeTab === 'coins' && (
            <div>
              <div className="text-center mb-6">
                <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  Coins power gameplay, generation credits, and premium features across LoopNest
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl mx-auto">
                {COIN_PACKS?.map((pack) => {
                  const isLoading = checkoutLoading === pack?.id;
                  return (
                    <div
                      key={pack?.id}
                      className="relative flex flex-col rounded-2xl p-6 transition-all"
                      style={{
                        background: 'var(--color-card)',
                        border: `2px solid ${pack?.popular ? pack?.color : 'var(--color-border)'}`,
                        boxShadow: pack?.popular ? `0 0 24px ${pack?.color}20` : 'none',
                      }}
                    >
                      {pack?.popular && (
                        <div
                          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold"
                          style={{ background: pack?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
                        >
                          Best Value
                        </div>
                      )}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${pack?.color}20` }}>
                          <Icon name="Coins" size={24} color={pack?.color} />
                        </div>
                        <div>
                          <p className="text-lg font-bold" style={{ color: pack?.color, fontFamily: 'var(--font-data)' }}>{pack?.priceDisplay}</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{pack?.name}</p>
                        </div>
                      </div>
                      <div
                        className="flex items-center gap-2 p-3 rounded-xl mb-4"
                        style={{ background: `${pack?.color}10`, border: `1px solid ${pack?.color}30` }}
                      >
                        <Icon name="Sparkles" size={16} color={pack?.color} />
                        <span className="text-sm font-bold" style={{ color: pack?.color, fontFamily: 'var(--font-data)' }}>
                          {isPlatformOwner ? '∞ Unlimited' : `${pack?.coins?.toLocaleString()} Coins`}
                        </span>
                      </div>
                      <p className="text-xs mb-5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                        {pack?.description}
                      </p>
                      <button
                        onClick={() => handleCheckout(pack?.id)}
                        disabled={isLoading || isPlatformOwner}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-60"
                        style={{ background: isPlatformOwner ? '#10B981' : pack?.color, color: 'white', fontFamily: 'var(--font-heading)' }}
                      >
                        {isPlatformOwner ? (
                          <><Icon name="ShieldCheck" size={14} color="white" /> Owner Access</>
                        ) : isLoading ? (
                          <><Icon name="Loader" size={14} color="white" /> Processing...</>
                        ) : (
                          <><Icon name="CreditCard" size={14} color="white" /> Buy {pack?.priceDisplay}</>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Coin usage info */}
              <div className="mt-8 p-5 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <p className="text-sm font-bold mb-3" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>How Coins Work</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { icon: 'Gamepad2', label: 'Gameplay', desc: 'Continue after crash, unlock characters' },
                    { icon: 'Wand2', label: 'AI Generation', desc: 'Power AI creations and exports' },
                    { icon: 'Star', label: 'Unlock Items', desc: 'Vehicles, skins, power-ups' },
                    { icon: 'Gift', label: 'Daily Rewards', desc: 'Earn free coins daily' },
                  ]?.map((item) => (
                    <div key={item?.label} className="flex flex-col gap-1.5 p-3 rounded-xl" style={{ background: 'var(--color-muted)' }}>
                      <Icon name={item?.icon} size={16} color="var(--color-primary)" />
                      <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.label}</p>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Generation Costs Tab */}
          {activeTab === 'generation' && (
            <div className="space-y-6">
              {/* Cost Overview */}
              <div className="p-5 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Cpu" size={18} color="var(--color-primary)" />
                  <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>GPU Generation Architecture</h2>
                </div>
                <p className="text-sm mb-4" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  LoopNest uses a low-cost scalable GPU pipeline capable of producing thousands of videos at approximately $0.03 per video using open models.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Image Generation', cost: '~$0.002', icon: 'Image', color: '#6366F1', desc: 'Stable Diffusion XL' },
                    { label: 'Animation Clip', cost: '~$0.01', icon: 'Film', color: '#F59E0B', desc: 'Stable Video Diffusion' },
                    { label: 'Video Assembly', cost: '~$0.01', icon: 'Layers', color: '#10B981', desc: 'FFmpeg pipeline' },
                    { label: 'Full Music Video', cost: '~$0.03–$0.05', icon: 'Music', color: '#F43F5E', desc: 'End-to-end pipeline' },
                  ]?.map((item) => (
                    <div key={item?.label} className="p-3 rounded-xl" style={{ background: `${item?.color}10`, border: `1px solid ${item?.color}25` }}>
                      <Icon name={item?.icon} size={16} color={item?.color} />
                      <p className="text-xs font-bold mt-2" style={{ color: item?.color, fontFamily: 'var(--font-data)' }}>{item?.cost}</p>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{item?.label}</p>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coin Costs */}
              <div className="p-5 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Coins" size={18} color="#F59E0B" />
                  <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Generation Coin Costs</h2>
                </div>
                <div className="space-y-3">
                  {[
                    { type: 'image_generation', label: 'Single Image Generation', coins: COIN_COSTS?.image_generation, icon: 'Image', color: '#6366F1', desc: 'Generate one cinematic scene image via Stable Diffusion' },
                    { type: 'animation_clip', label: 'Animation Clip', coins: COIN_COSTS?.animation_clip, icon: 'Film', color: '#F59E0B', desc: 'Animate one image into a short video clip via Stable Video Diffusion' },
                    { type: 'scene_batch', label: 'Scene Batch (6 scenes)', coins: COIN_COSTS?.scene_batch, icon: 'Grid3X3', color: '#10B981', desc: 'Generate a batch of 6 scene images for a music video' },
                    { type: 'full_music_video', label: 'Full Music Video', coins: COIN_COSTS?.full_music_video, icon: 'Music', color: '#F43F5E', desc: 'Complete pipeline: analyze music → generate scenes → animate → assemble final video' },
                  ]?.map((item) => (
                    <div key={item?.type} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--color-muted)' }}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item?.color}20` }}>
                        <Icon name={item?.icon} size={16} color={item?.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.label}</p>
                        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{item?.desc}</p>
                      </div>
                      <div className="flex items-center gap-1 px-3 py-1.5 rounded-full flex-shrink-0" style={{ background: `${item?.color}15`, border: `1px solid ${item?.color}30` }}>
                        <Icon name="Coins" size={12} color={item?.color} />
                        <span className="text-sm font-bold" style={{ color: item?.color, fontFamily: 'var(--font-data)' }}>{item?.coins}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pipeline Steps */}
              <div className="p-5 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="GitBranch" size={18} color="var(--color-primary)" />
                  <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Video Generation Pipeline</h2>
                </div>
                <div className="space-y-2">
                  {[
                    { step: 1, label: 'Analyze Music', desc: 'OpenAI extracts BPM, rhythm, mood, genre, and scene themes from your audio', icon: 'AudioLines', color: '#6366F1' },
                    { step: 2, label: 'Generate Scene Prompts', desc: 'AI creates 6–12 cinematic scene descriptions tailored to your music', icon: 'Wand2', color: '#8B5CF6' },
                    { step: 3, label: 'Generate Scene Images', desc: 'Stable Diffusion XL renders photorealistic keyframe images (~$0.002 each)', icon: 'Image', color: '#F59E0B' },
                    { step: 4, label: 'Animate Scenes', desc: 'Stable Video Diffusion animates each image into a short video clip (~$0.01 each)', icon: 'Film', color: '#10B981' },
                    { step: 5, label: 'Assemble Video', desc: 'Clips are sequenced and synchronized to your music BPM', icon: 'Layers', color: '#06B6D4' },
                    { step: 6, label: 'Attach Audio', desc: 'Original audio track is merged with the final video', icon: 'Music', color: '#F43F5E' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: 'var(--color-muted)' }}>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ background: `${item?.color}20`, color: item?.color, fontFamily: 'var(--font-data)' }}>
                        {item?.step}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.label}</p>
                        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{item?.desc}</p>
                      </div>
                      <Icon name={item?.icon} size={14} color={item?.color} className="flex-shrink-0 mt-0.5" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Free Plan Limits */}
              <div className="p-5 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid rgba(245,158,11,0.3)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Shield" size={18} color="#F59E0B" />
                  <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Plan Generation Limits</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        {['Feature', 'Free', 'Pro', 'Studio', 'Enterprise']?.map((h) => (
                          <th key={h} className="text-left py-2 pr-4 font-semibold" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="space-y-1">
                      {[
                        { feature: 'Videos / day', free: '1', pro: '10', studio: '50', enterprise: 'Unlimited' },
                        { feature: 'Max duration', free: '2 min', pro: '5 min', studio: '10 min', enterprise: 'Unlimited' },
                        { feature: 'Watermark', free: '✓', pro: '✗', studio: '✗', enterprise: '✗' },
                        { feature: 'HD Export', free: '✗', pro: '✓', studio: '✓', enterprise: '✓' },
                        { feature: 'Priority GPU', free: '✗', pro: '✓', studio: '✓', enterprise: '✓' },
                        { feature: 'Starting coins', free: '50', pro: '200', studio: '500', enterprise: '9,999' },
                      ]?.map((row) => (
                        <tr key={row?.feature} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td className="py-2 pr-4 font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{row?.feature}</td>
                          {['free', 'pro', 'studio', 'enterprise']?.map((tier) => (
                            <td key={tier} className="py-2 pr-4" style={{
                              color: row?.[tier] === '✓' ? '#10B981' : row?.[tier] === '✗' ? '#EF4444' : 'var(--color-muted-foreground)',
                              fontFamily: 'var(--font-data)',
                            }}>{row?.[tier]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Coin Transaction History */}
              {isAuthenticated && coinTransactions?.length > 0 && (
                <div className="p-5 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="History" size={18} color="var(--color-primary)" />
                    <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Recent Coin Usage</h2>
                  </div>
                  <div className="space-y-2">
                    {coinTransactions?.slice(0, 8)?.map((t) => (
                      <div key={t?.id} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--color-muted)' }}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0`} style={{ background: t?.action === 'deduct' ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)' }}>
                          <Icon name={t?.action === 'deduct' ? 'Minus' : 'Plus'} size={10} color={t?.action === 'deduct' ? '#EF4444' : '#10B981'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{t?.reason}</p>
                          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Balance: {t?.balanceAfter?.toLocaleString()}</p>
                        </div>
                        <span className="text-xs font-bold flex-shrink-0" style={{ color: t?.action === 'deduct' ? '#EF4444' : '#10B981', fontFamily: 'var(--font-data)' }}>
                          {t?.action === 'deduct' ? '-' : '+'}{t?.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Purchase History Tab */}
          {activeTab === 'history' && (
            <div>
              {!isAuthenticated ? (
                <div className="text-center py-12">
                  <Icon name="Lock" size={40} color="var(--color-muted-foreground)" />
                  <p className="mt-3 text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Sign in to view your purchase history</p>
                </div>
              ) : isPlatformOwner ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(16,185,129,0.15)' }}>
                    <Icon name="ShieldCheck" size={32} color="#10B981" />
                  </div>
                  <p className="text-base font-bold mb-2" style={{ color: '#10B981', fontFamily: 'var(--font-heading)' }}>Platform Owner Mode</p>
                  <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                    This account has permanent enterprise access. No billing history applies.
                  </p>
                </div>
              ) : loadingData ? (
                <div className="text-center py-12">
                  <Icon name="Loader" size={32} color="var(--color-primary)" />
                  <p className="mt-3 text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Loading history...</p>
                </div>
              ) : purchaseHistory?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Receipt" size={40} color="var(--color-muted-foreground)" />
                  <p className="mt-3 text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>No purchases yet</p>
                  <button
                    onClick={() => setActiveTab('coins')}
                    className="mt-4 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                    style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}
                  >
                    Buy Coins
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {purchaseHistory?.map((p) => (
                    <div
                      key={p?.id}
                      className="flex items-center justify-between p-4 rounded-xl"
                      style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{ background: p?.type === 'coin_pack' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.15)' }}
                        >
                          <Icon name={p?.type === 'coin_pack' ? 'Coins' : 'Crown'} size={16} color={p?.type === 'coin_pack' ? '#F59E0B' : '#6366F1'} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                            {p?.description || (p?.type === 'coin_pack' ? `${p?.coinsGranted} Coins` : `${p?.tierGranted} Subscription`)}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                            {new Date(p.createdAt)?.toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {p?.coinsGranted > 0 && (
                          <span className="text-xs font-bold" style={{ color: '#F59E0B', fontFamily: 'var(--font-data)' }}>+{p?.coinsGranted} coins</span>
                        )}
                        <span className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>
                          ${(p?.amountCents / 100)?.toFixed(2)}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{
                            background: p?.status === 'succeeded' ? 'rgba(16,185,129,0.15)' : p?.status === 'pending' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                            color: p?.status === 'succeeded' ? '#10B981' : p?.status === 'pending' ? '#F59E0B' : '#EF4444',
                            fontFamily: 'var(--font-caption)',
                          }}
                        >
                          {p?.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer note */}
          <div className="mt-8 text-center">
            {isPlatformOwner ? (
              <p className="text-xs" style={{ color: '#10B981', fontFamily: 'var(--font-caption)' }}>
                🛡️ Platform Owner Mode — Full enterprise access is permanently active. Normal billing applies to all other users.
              </p>
            ) : (
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                Payments are processed securely by Stripe. Cancel subscriptions anytime from your account settings.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
